import { useState } from 'react';
import { Environment } from '@components/community/constants';

const NEWSLETTER_FORM_TYPE = 'community_learning_signup';
const NEWSLETTER_FORM_DEF = 'pointALead';

const WAC_BASE_URL = {
  [Environment.LOCAL]: '/api/wac',
  [Environment.DEVELOPMENT]: 'https://wac.stg.internal.atlassian.com', //  WAC dev env is unavailable for this endpoint
  [Environment.STAGING]: 'https://wac.stg.internal.atlassian.com',
  [Environment.PRODUCTION]: 'https://www.atlassian.com',
};

export type NewsletterFormValues = {
  email: string;
  firstname?: string;
  lastname?: string;
  anonymousId?: string;
  atlassianId?: string;
};

interface Props {
  environment?: Environment;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

export const usePostNewsletterSignup = ({
  environment = Environment.DEVELOPMENT,
  onError = () => null,
  onSuccess = () => null,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleNewsletterSignup = async (formValues: NewsletterFormValues, formUrl?: string): Promise<void> => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const preferencesResponse = await postPreferencesUpdate(formValues, environment, formUrl);

      if (!preferencesResponse.ok || preferencesResponse.status !== 202) {
        throw new Error(`Error in postPreferencesUpdate with status: ${preferencesResponse.status}`);
      }

      const wacResponse = await postWacFormLead(formValues, environment);

      if (!wacResponse.ok || wacResponse.status !== 200) {
        throw new Error(`Error in postWacFormLead with status: ${wacResponse.status}`);
      }

      onSuccess();
      setSuccess(true);
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      }

      onError(error);
    } finally {
      setLoading(false);
    }
  };

  return { handleNewsletterSignup, loading, error, success };
};

const postPreferencesUpdate = async (
  formValues: NewsletterFormValues,
  environment: Environment,
  formUrl?: string,
): Promise<Response> => {
  const locale = navigator.language || Intl.DateTimeFormat().resolvedOptions().locale;
  const region = locale.split('-')[1];

  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      consentCheckboxValue: true,
      consentLabelText:
        'By signing up, you agree to receive promotional emails including product news, events, and more from Atlassian and its affiliates, and acknowledge and accept the Atlassian Privacy Policy.',
      email: formValues.email,
      formUrl: formUrl || 'https://community.atlassian.com/',
      formId: '655e8a50-2d1f-4c1c-a782-fb5333e272a8',
      locale: region,
      paramsLocale: region,
      privacyPolicyText:
        '<p>By signing up, you agree to receive promotional emails including product news, events, and more from Atlassian and its affiliates, and acknowledge and accept the Atlassian Privacy Policy. <a href=\\"https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers\\">Atlassian Privacy Policy</a>.</p>\n',
      site: 'atlassian',
    }),
  };

  return fetch(WAC_BASE_URL[environment] + '/gateway/api/preferencesV2/update', requestOptions);
};

// Initiates POST to /streamhub/wacformlead
// Endpoint in: https://bitbucket.org/atlassian/bxp-express/src/routes/streamhubSubmission.ts
const postWacFormLead = async (formValues: NewsletterFormValues, environment: Environment): Promise<Response> => {
  const now = new Date();

  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...formValues,
      formType: NEWSLETTER_FORM_TYPE,
      formDef: NEWSLETTER_FORM_DEF,
      created_at: now.toISOString(),
      submitted: now.toString(),
    }),
  };

  return fetch(WAC_BASE_URL[environment] + '/endpoint/streamhub/wacformlead', requestOptions);
};
